/*
  Copy for the formal verification page, taken verbatim from the approved mockup.
  It lives apart from the client component so the server-rendered page can read
  `faqs` for its FAQPage JSON-LD: both then use the same source and cannot drift.
*/

export const railItems = [
  { number: "01", title: "Clear requirements", description: "Define what must hold" },
  { number: "02", title: "Verification evidence", description: "Properties, assumptions, and results" },
  { number: "03", title: "Checks that follow change", description: "Recheck affected properties" },
];

export const questions = [
  {
    number: "01 /",
    question: "Can funds move without the required approvals?",
    answer: "Check authorization across contracts, services, and permissions.",
  },
  {
    number: "02 /",
    question: "Can an upgrade break an accounting invariant?",
    answer: "Verify balances, liabilities, and settlement rules.",
  },
  {
    number: "03 /",
    question: "Can a configuration change weaken access controls?",
    answer: "Check permissions and approval thresholds against agreed rules.",
  },
];

export const steps = [
  {
    label: "01 / Define",
    title: "Define the properties.",
    description: "Select critical workflows with your engineers. Agree on the properties to verify and the model assumptions.",
  },
  {
    label: "02 / Establish",
    title: "Establish a baseline.",
    description: "Formalize the properties, run verification, and investigate failures and gaps.",
  },
  {
    label: "03 / Connect",
    title: "Connect your workflow.",
    description: "Connect repositories and automate checks. Set escalation rules for findings and changes in scope.",
  },
  {
    label: "04 / Maintain",
    title: "Recheck affected properties.",
    description: "Rerun affected checks. Flag changed assumptions and changes the model does not cover.",
  },
];

export const roles = [
  { name: "Your team", description: "Defines intended behavior and approves scope, exceptions, and risk decisions." },
  {
    name: "Our experts",
    description: "Help define and validate specifications and models. Investigate findings and conduct scoped reviews.",
  },
  { name: "AI assistance", description: "Drafts properties and assists with proof work. Formal tools check the claims." },
  {
    name: "Verification cloud",
    description: "Runs verification tools, reuses valid results, and reruns affected checks within agreed compute budgets.",
  },
];

export const workflows = [
  {
    symbol: "⌘",
    title: "Authorization & custody",
    description: "Approval thresholds, role separation, privileged access, and asset transfers.",
  },
  {
    symbol: "≡",
    title: "Accounting & settlement",
    description: "Balances, liabilities, settlement conditions, and valid state transitions.",
  },
  { symbol: "↻", title: "Protocol upgrades", description: "Invariants across upgrades, migrations, and configuration changes." },
  { symbol: "⇄", title: "Cross-system workflows", description: "Rules spanning contracts, services, and modeled dependencies." },
  { symbol: "⊢", title: "AI agent permissions", description: "Tool access, transaction limits, and required approvals." },
  { symbol: "◇", title: "Critical application logic", description: "Business rules, state transitions, and failure conditions." },
];

export const deliverables = [
  { title: "A property register", description: "Properties, assumptions, scope, and owners." },
  { title: "Dependency mapping", description: "Links from properties to code, configuration, and dependencies." },
  { title: "Versioned verification evidence", description: "Results, available proof artifacts, and reproducible records." },
  { title: "Findings and exceptions", description: "Counterexamples, unresolved claims, and review decisions." },
  { title: "Checks in your release workflow", description: "Automated checks and clear escalation rules." },
];

export const faqs = [
  {
    question: "What does formal verification establish?",
    answer:
      "It establishes whether specified properties hold within a model and its assumptions. Expert validation helps assess whether those properties reflect your intended behavior.",
  },
  {
    question: "What is checked continuously?",
    answer:
      "Agreed properties are rechecked when relevant changes occur. Requirements and models are defined at setup and revisited through scoped reviews as needed.",
  },
  {
    question: "How is this different from an audit or monitoring?",
    answer:
      "An audit assesses a version of your system. Monitoring observes live activity. Continuous verification rechecks specified properties as the system changes.",
  },
  {
    question: "How is AI used?",
    answer: "AI assists with specifications and proof work. Formal tools check the claims; experts define the approach and investigate findings.",
  },
  {
    question: "Can we start with one contract or workflow?",
    answer: "Yes. Start with one contract or workflow, establish a baseline, and expand coverage as needed.",
  },
  {
    question: "What do you need to scope an engagement?",
    answer: "Your critical workflow, relevant code and documentation, key requirements, and release process.",
  },
];
